import { Icon } from "@circle-react-shared/Icon";
import { PlanSelector as PlanSelectorSkeleton } from "@circle-react-uikit/SkeletonLoader/skeletons/PlanSelector";
import { useCommunityIsIcp } from "@circle-react/components/CommunityOnboarding/NewChecklistComponents/CircleAppLinks/useCommunityIsIcp";
import { usePlans } from "@circle-react/components/SettingsApp/Plan/RegularPlan/PlansContext";
import { Typography } from "@circleco/compass/components/Typography";
import { RadioGroup } from "@headlessui/react-v2";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { isDowngrading, planTiers } from "@/react/helpers/planHelpers";
import { useEventsTracker } from "@/react/hooks/analytics/useEventsTracker";
import { usePlanPageVariant } from "@/react/hooks/usePlanPageVariant";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import type { Plan } from "@/react/types/Plan";

import { PlanSelectorTitle } from "../experiment/PlanSelectorTitle";
import { PlanCard } from "./PlanCard";
import { PlusCard } from "./PlusCard";

export const PlanSelector = () => {
  const {
    plans,
    selectedPlan,
    selectedPlanTier,
    setSelectedPlanTier,
    customPlanOfferPlan,
  } = usePlans();
  const {
    isCommunityOnPaidPlan,
    currentPlanTier,
    isCommunityOnTrial,
    isCommunityOnBusiness,
    isCommunityOnEnterprise,
  } = usePlanStatus();
  const isCommunityIsIcp = useCommunityIsIcp();
  const { currentCommunity } = usePunditUserContext();
  const hasActiveCommunityCoupon =
    !!currentCommunity?.has_active_community_coupon;
  // A custom Plus offer is always shown. Otherwise, trial communities that
  // signed up with a coupon don't see Plus as an option.
  const shouldShowPlusCard =
    !!customPlanOfferPlan ||
    (!hasActiveCommunityCoupon &&
      (isCommunityIsIcp || isCommunityOnBusiness || isCommunityOnEnterprise));
  const [areAllPlansVisible, setAreAllPlansVisible] = useState(false);
  const { trackEvent } = useEventsTracker();

  const onPlanClick = useCallback(
    (planTier: Plan["tier"]) => {
      trackEvent("plan_settings.plan.clicked", {
        plan_tier: planTier,
      });
      setSelectedPlanTier(planTier);
    },
    [trackEvent, setSelectedPlanTier],
  );

  const hiddenPlansRef = useRef<HTMLDivElement>(null);
  const [hiddenPlansHeight, setHiddenPlansHeight] = useState<number>(0);
  const { isDesktopLayout } = usePlanPageVariant();

  const sortedPlans = useMemo(() => [...plans].reverse(), [plans]);

  const { visiblePlans, hiddenPlans } = useMemo(() => {
    if (areAllPlansVisible) {
      return {
        visiblePlans: sortedPlans,
        hiddenPlans: [],
      };
    }

    const isHidden = (plan: Plan) => {
      if (isCommunityOnTrial) return plan.tier === planTiers.basic;
      if (isCommunityOnPaidPlan && currentPlanTier)
        return isDowngrading(currentPlanTier, plan.tier);
      return false;
    };

    return {
      visiblePlans: sortedPlans.filter(plan => !isHidden(plan)),
      hiddenPlans: sortedPlans.filter(plan => isHidden(plan)),
    };
  }, [
    sortedPlans,
    areAllPlansVisible,
    isCommunityOnTrial,
    isCommunityOnPaidPlan,
    currentPlanTier,
  ]);

  useEffect(() => {
    if (areAllPlansVisible && hiddenPlansRef.current) {
      setHiddenPlansHeight(hiddenPlansRef.current.scrollHeight);
    } else {
      setHiddenPlansHeight(0);
    }
  }, [areAllPlansVisible, hiddenPlans.length]);

  if (plans.length === 0 || !selectedPlanTier) {
    return <PlanSelectorSkeleton cardCount={3} />;
  }

  return (
    <RadioGroup value={selectedPlanTier} onChange={onPlanClick}>
      <div className="flex w-full justify-center">
        <ul className="border-primary flex w-full flex-col overflow-hidden rounded-xl border">
          {isDesktopLayout && <PlanSelectorTitle />}

          {shouldShowPlusCard && (
            <PlusCard
              selected={selectedPlanTier === planTiers.plus}
              onClick={() => onPlanClick(planTiers.plus)}
              showPrice={!isCommunityIsIcp}
            />
          )}

          {visiblePlans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              selected={plan.tier === selectedPlanTier}
              onClick={() => onPlanClick(plan.tier)}
            />
          ))}

          {hiddenPlans.length > 0 && (
            <div
              ref={hiddenPlansRef}
              className="overflow-hidden duration-300 ease-in-out"
              style={{ height: `${hiddenPlansHeight}px` }}
            >
              {hiddenPlans.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selected={plan.tier === selectedPlan?.tier}
                  onClick={() => setSelectedPlanTier(plan.tier)}
                />
              ))}
            </div>
          )}

          {hiddenPlans.length > 0 && (
            <button
              type="button"
              className="bg-primary flex w-full cursor-pointer flex-row justify-start gap-0 px-6 py-4"
              onClick={() => setAreAllPlansVisible(!areAllPlansVisible)}
            >
              <Typography variant="label-xs">
                {areAllPlansVisible
                  ? t(
                      "pricing_plans.plan_settings.plan_selection.plan_selector.see_less_plans",
                    )
                  : t(
                      "pricing_plans.plan_settings.plan_selection.plan_selector.see_more_plans",
                    )}
              </Typography>
              <Icon
                type={areAllPlansVisible ? "16-chevron-up" : "16-chevron-down"}
                size={12}
                className="ml-2"
              />
            </button>
          )}
        </ul>
      </div>
    </RadioGroup>
  );
};
